import ProductService from "@/services/product.service"

const initialState = {
    products: [],
    product: null,
    productItems: [],
    itemImages: [],
    productAttributeGroups: null,
    categories: [],
    brands: [],
    attributeGroups: [],
    bulkAttributeValues: []
};

export const product = {
    namespaced: true,
    state: initialState,
    actions: {
        async getProducts({ commit }) {
            try {
                const products = await ProductService.getProducts();
                commit('successProducts', products);
                return products;
            } catch (error) {
                console.error("Error fetching products:", error);
                throw error;
            }
        },
        async getProduct({ commit }, id) {
            try {
                const product = await ProductService.getProduct(id);
                commit('successProduct', product);
                return product;
            } catch (error) {
                console.error("Error fetching product:", error);
                throw error;
            }
        },
        async getProductAttributeGroups({ commit }, id) {
            try {
                const groups = await ProductService.getProductAttributeGroups(id);
                commit('successProductAttributeGroups', groups);
                return groups;
            } catch (error) {
                console.error("Error fetching product attribute groups:", error);
                throw error;
            }
        },
        async getProductItems({ commit }, productId) {
            try {
                const productItems = await ProductService.getProductItems(productId);
                commit('successProductItems', productItems);
                return productItems;
            } catch (error) {
                console.error("Error fetching product items:", error);
                throw error;
            }
        },
        async getItemImages({ commit }, itemId) {
            try {
                const images = await ProductService.getItemImages(itemId);
                commit('successItemImages', images);
                return images;
            } catch (error) {
                console.error("Error fetching item images:", error);
                throw error;
            }
        },
        async getCommonImage({ productId, attributeValueId, coverOrder }) {
            try {
                const response = await ProductService.getCommonImage(productId, attributeValueId, coverOrder);
                return response.imageName;
            } catch (error) {
                console.error("Error fetching common image:", error);
                throw error;
            }
        },
        async getCategories({ commit }) {
            try {
                const categories = await ProductService.getCategories();
                commit('successCategories', categories);
                return categories;
            } catch (error) {
                console.error("Error fetching categories:", error);
                throw error;
            }
        },
        async getBrands({ commit }) {
            try {
                const brands = await ProductService.getBrands();
                commit('successBrands', brands);
                return brands;
            } catch (error) {
                console.error("Error fetching brands:", error);
                throw error;
            }
        },
        async getAttributeGroups({ commit }) {
            try {
                const attributeGroups = await ProductService.getAttributeGroups();
                commit('successAttributeGroups', attributeGroups);
                return attributeGroups;
            } catch (error) {
                console.error("Error fetching attribute groups:", error);
                throw error;
            }
        },
        async getBulkAttributeValues({ commit }, groupId) {
            try {
                const values = await ProductService.getAttributeValues(groupId);
                commit('successBulkAttributeValues', values);
                return values;
            } catch (error) {
                console.error("Error fetching attribute values:", error);
                throw error;
            }
        },
        clearBulkAttributeValues({ commit }) {
            commit('successBulkAttributeValues', []);
        },
        async createAttributeValue({ dispatch }, attributeValueDTO) {
            try {
                await ProductService.createAttributeValue(attributeValueDTO);
                return await dispatch('getBulkAttributeValues', attributeValueDTO.attributeGroupId);
            } catch (error) {
                console.error("Error creating attribute value:", error);
                throw error;
            }
        },
        async updateAttributeValue({ dispatch }, { id, attributeValueDTO }) {
            try {
                await ProductService.updateAttributeValue(id, attributeValueDTO);
                return await dispatch('getBulkAttributeValues', attributeValueDTO.attributeGroupId);
            } catch (error) {
                console.error("Error updating attribute value:", error);
                throw error;
            }
        },
        async deleteAttributeValue({ dispatch }, { id, attributeGroupId }) {
            try {
                await ProductService.deleteAttributeValue(id);
                return await dispatch('getBulkAttributeValues', attributeGroupId);
            } catch (error) {
                console.error("Error deleting attribute value:", error);
                throw error;
            }
        },
        async createAttributeGroup({ dispatch }, attributeGroupDTO) {
            try {
                await ProductService.createAttributeGroup(attributeGroupDTO);
                return await dispatch('getAttributeGroups');
            } catch (error) {
                console.error("Error creating attribute group:", error);
                throw error;
            }
        },
        async updateAttributeGroup({ dispatch }, { id, attributeGroupDTO }) {
            try {
                await ProductService.updateAttributeGroup(id, attributeGroupDTO);
                return await dispatch('getAttributeGroups');
            } catch (error) {
                console.error("Error updating attribute group:", error);
                throw error;
            }
        },
        async uploadItemPhoto({ dispatch }, { itemId, file, coverOrder }) {
            try {
                await ProductService.uploadItemPhoto(itemId, file, coverOrder);
                return await dispatch('getItemImages', itemId);
            } catch (error) {
                console.error("Error uploading item photo:", error);
                throw error;
            }
        },
        async uploadItemPhotoByUrl({ dispatch }, { itemId, url, coverOrder }) {
            try {
                await ProductService.uploadItemPhotoByUrl(itemId, url, coverOrder);
                return await dispatch('getItemImages', itemId);
            } catch (error) {
                console.error("Error uploading item photo by URL:", error);
                throw error;
            }
        },
        async uploadPhotoByAttributeGroup({ dispatch }, { productId, attributeGroupId, file, coverOrder }) {
            try {
                await ProductService.uploadPhotoByAttributeGroup(productId, attributeGroupId, file, coverOrder);
                return await dispatch('getProductItems', productId);
            } catch (error) {
                console.error("Error uploading photo by attribute group:", error);
                throw error;
            }
        },
        async uploadPhotoByAttributeGroupByUrl({ dispatch }, { productId, attributeGroupId, url, coverOrder }) {
            try {
                await ProductService.uploadPhotoByAttributeGroupByUrl(productId, attributeGroupId, url, coverOrder);
                return await dispatch('getProductItems', productId);
            } catch (error) {
                console.error("Error uploading photo by attribute group by URL:", error);
                throw error;
            }
        },
        async updateProduct({ commit }, { id, productDTO }) {
            try {
                const updatedProduct = await ProductService.updateProduct(id, productDTO);
                commit('successProduct', updatedProduct);
                return updatedProduct;
            } catch (error) {
                console.error("Error updating product:", error);
                throw error;
            }
        },
        async updateProductItem({ commit }, { id, itemDTO }) {
            try {
                const updatedItem = await ProductService.updateProductItem(id, itemDTO);
                commit('updateItemInList', updatedItem);
                return updatedItem;
            } catch (error) {
                console.error("Error updating product item:", error);
                throw error;
            }
        },
        async deleteProduct({ commit }, productId) {
            try {
                await ProductService.deleteProduct(productId);
                commit('deleteFromProducts', productId);
            } catch (error) {
                console.error("Error deleting product:", error);
                throw error;
            }
        }
    },
    mutations: {
        successProducts(state, products) {
            state.products = products;
        },
        successProduct(state, product) {
            state.product = product;
        },
        successProductAttributeGroups(state, groups) {
            state.productAttributeGroups = groups;
        },
        successProductItems(state, productItems) {
            state.productItems = productItems;
        },
        successItemImages(state, images) {
            state.itemImages = images;
        },
        successCategories(state, categories) {
            state.categories = categories;
        },
        successBrands(state, brands) {
            state.brands = brands;
        },
        successAttributeGroups(state, attributeGroups) {
            state.attributeGroups = attributeGroups;
        },
        successBulkAttributeValues(state, values) {
            state.bulkAttributeValues = values;
        },
        updateItemInList(state, updatedItem) {
            const index = state.productItems.findIndex(item => item.id === updatedItem.id);
            if (index !== -1) {
                state.productItems[index] = updatedItem;
            }
        },
        deleteFromProducts(state, productId) {
            state.products = state.products.filter(p => p.id !== productId);
        }
    },
    getters: {
        getProducts(state) {
            return state.products;
        },
        getProduct(state) {
            return state.product;
        },
        getProductAttributeGroups(state) {
            return state.productAttributeGroups;
        },
        getProductItems(state) {
            return state.productItems;
        },
        getItemImages(state) {
            return state.itemImages;
        },
        getCategories(state) {
            return state.categories;
        },
        getBrands(state) {
            return state.brands;
        },
        getAttributeGroups(state) {
            return state.attributeGroups;
        },
        getBulkAttributeValues(state) {
            return state.bulkAttributeValues;
        }
    }
}
